<template>
  <div class="receipt-table table-component">

    <artisio-table :pagination="tableData.pagination" :sort="tableData.ordering" :items="tableData.rows"
                   :busy="tableData.loading" :fields="tableFields" :checkboxes="true" :search="tableData.search"
                   :enable-simple-search="false" export-vuex-module-name="receipts" enable-frontend-export
                   export-permission-name="export_receipt" :export-attributes="exportAttributes"
                   @paramsChanged="getTableData" selectable filter-toggle-string="receipts-table"
                   show-toggle-columns :extra-data="extraData" @update-toggle-columns="updateToggleColumns"
                   @on-column-save="updateToggleColumns">

      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown :text="$t('Actions')" variant="primary" class="mr-2"
                    v-permission="'change_receipt'">
          <b-dropdown-item @click="generateQrCodes(selectedItems)"
                           :disabled="selectedItems.size === 0 || qrCodeGenerationLoading">
            <b-spinner style="width: 1rem; height: 1rem" class="mr-2" v-if="qrCodeGenerationLoading"/>
            {{ $t('Generate QR Codes') }}
          </b-dropdown-item>
        </b-dropdown>

        <delete-button v-permission="'delete_auction' " :disabled="selectedItems.size === 0"
                       @click="onBatchRemoveClick(selectedItems)"/>
      </template>

      <template v-slot:search-form-placeholder>
        <search-collapse table-id="receipts-table">
          <receipt-search/>
        </search-collapse>
      </template>

      <template slot="cell(receipt_no)" slot-scope="{item: receipt}">
        {{ receipt.receipt_no }}
        <action-buttons :delete-permission="'delete_receipt'"
                        :edit-permission="'change_receipt'"
                        :view-permission="'view_receipt'"
                        @viewClick="onViewClick(receipt)"
                        @editClick="onEditClick(receipt)"
                        @deleteClick="onRemoveClick(receipt)"/>
      </template>

      <template slot="cell(customer)" slot-scope="{item: receipt}">
        <router-link v-if="receipt.customer && $hasPermission('view_customer')"
                     :to="{ name: 'customers.dashboard', params: { id: receipt.customer_uuid }}">
          {{ receipt.customer.display_name }}
        </router-link>
        <p v-else-if="receipt.customer"> {{ receipt.customer.display_name }} </p>
      </template>

      <template slot="cell(items)" slot-scope="{item: receipt}">
        <router-link :to="{name: 'receipt.inventory',params: {id: receipt.uuid}}">
          <b-badge variant="primary" class="hover-cursor">
            {{ $t('{item_count} items', {item_count: receipt.total_items || 0}) }}
          </b-badge>
        </router-link>
      </template>

      <template slot="cell(reserve)" slot-scope="{item: receipt}">
        {{ receipt.total_reserve | asCurrency }}
      </template>

      <template slot="cell(date)" slot-scope="{item: receipt}">
        <template v-if="receipt.date">
          {{ receipt.date | toDate }}
        </template>
      </template>

      <template slot="cell(dropper_uuid)" slot-scope="{item: receipt}">
        <template v-if="receipt.dropper">
          {{ receipt.dropper.display_name }}
        </template>
      </template>

      <template slot="cell(description)" slot-scope="{item: receipt}">
        <template v-if="receipt.description">
          {{ receipt.description }}
        </template>
      </template>

      <template slot="cell(recommended_by_user)" slot-scope="{item: receipt}">
        <template v-if="receipt.recommended_by_user">
          {{ receipt.recommended_by_user.display_name }}
        </template>
      </template>

      <template slot="cell(specialists)" slot-scope="{item: receipt}">
        <template v-if="receipt.specialists">
          {{ receipt.specialists.map(s => s.display_name).join(', ') }}
        </template>
      </template>

      <template slot="cell(department)" slot-scope="{item: receipt}">
        {{ getDepartmentValue(receipt) }}
      </template>

      <template slot="cell(item_location)" slot-scope="{item: receipt}">
        <receipt-item-location :itemLocations="receipt.item_locations" />
      </template>

      <template slot="cell(enable_multiple_locations)" slot-scope="{item}">
        <span class="badge bg-success text-white" v-if="item.enable_multiple_locations">{{ $t('Yes') }}</span>
        <span class="badge bg-danger text-white" v-else>{{ $t('No') }}</span>
      </template>

      <template slot="cell(minimum_commission)" slot-scope="{item: receipt}">
        {{ receipt.minimum_commission | asCurrency }}
      </template>

      <template slot="cell(insurance_rate)" slot-scope="{item: receipt}">
        {{ receipt.insurance_rate | asPercentage }}
      </template>

      <template slot="cell(lot_handling_charge)" slot-scope="{item: receipt}">
        {{ receipt.lot_handling_charge | asCurrency }}
      </template>

      <template slot="cell(created_data)" slot-scope="{item: receipt}">
        {{ receipt.created_at | toDate }} by {{ receipt.created_by.display_name }}
      </template>

      <template slot="cell(updated_data)" slot-scope="{item: receipt}">
        {{ receipt.updated_at | toDate }} by {{ receipt.updated_by.display_name }}
      </template>

      <template slot="cell(status)" slot-scope="{item: receipt}">
        <span v-if="receipt.status" v-as-status="receipt.status">
          {{ getStatusLabel(receipt.status) | translate }}
        </span>
      </template>
      <template slot="cell(rep_user)" slot-scope="{item: receipt}">
        <span v-if="receipt.rep_user">
          {{ receipt.rep_user.display_name }}
        </span>
      </template>


      <template slot="cell(commissions)" slot-scope="{item: receipt}">
        <template v-for="commission in getCalculatedCommissions(receipt.commissions)">
          {{ commission.percent | asPercentage }} - {{ commission.amount_over | asCurrency }}
          <br/>
        </template>
      </template>

      <template slot="cell(receipt_contract_status)" slot-scope="{item: receipt}">
        <span v-if="receipt.receipt_contract_status" v-as-status="receipt.receipt_contract_status">
          {{ receipt.receipt_contract_status | removeUnderscores | toUpperCaseEveryWord | translate }}
        </span>
        <span v-as-status="receipt.receipt_contract_status" v-else>{{ $t('N/A') }}</span>
      </template>

      <template :slot="`cell(${dynamicField})`" slot-scope="{item, field}"
                v-for="dynamicField in toggleColumns.dynamicFields">
        <span
          v-html="$options.filters.asDynamicField(getDynamicFieldValue(item, dynamicField), field.type, field.choices)">
        </span>
      </template>


      <template slot="cell(pre_receipt_contract_status)" slot-scope="{item: receipt}">
        <span v-if="receipt.pre_receipt_contract_status" v-as-status="receipt.pre_receipt_contract_status">
          {{ receipt.pre_receipt_contract_status | removeUnderscores | toUpperCaseEveryWord }}
        </span>
        <span v-as-status="receipt.pre_receipt_contract_status" v-else>{{ $t('N/A') }}</span>
      </template>

      <template slot="cell(receipt_type)" slot-scope="{item: receipt}">
        {{ receipt.receipt_type | removeUnderscores | toUpperCaseEveryWord }}
      </template>

      <template slot="cell(internal_images)" slot-scope="{item: receipt}">
        <div class="position-relative">
          <internal-images-viewer :internal-images="receipt.internal_images"
                                  v-if="receipt.internal_images && receipt.internal_images.length > 0"/>
          <div class="attachments-numbering" v-if="receipt.internal_images && receipt.internal_images.length > 1">
            +{{ receipt.internal_images.length - 1}}
          </div>
        </div>
      </template>
      <template slot="cell(attachments)" slot-scope="{item: receipt}">
        <a href="javascript:void(0)" @click="onAttachmentsViewClick(receipt)"
           :class="!receipt.attachments || receipt.attachments.length === 0 ? 'text-muted' : ''"
           v-if="$hasPermission('view_receipt')">
          {{ $t('{count} attachment(s)', {count: receipt.attachments ? receipt.attachments.length : 0}) }}
        </a>
      </template>

    </artisio-table>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import {RECEIPTS_TABLE_NAME, SOURCE_RECEIPT_TABLE} from "../constants";
  import ActionButtons from "../../core/components/ams-table/ActionButtons";
  import ReceiptSearch from "./ReceiptSearch";
  import SearchCollapse from "../../core/components/search-collapse/SearchCollapse";
  import DeleteButton from "../../core/components/common/DeleteButton";
  import i18n from "../../shared/i18n";
  import helperService from "@/core/services/helper.service";
  import cloneDeep from "lodash/cloneDeep";
  import QrCodeService from "@/core/components/qr-code-generator/QrCodeService";
  import {ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import ReceiptItemLocation from "@/modules/receipt/components/ReceiptItemLocation.vue";
  import InternalImagesViewer from "@/core/components/attachment-image-viewer/InternalImagesViewer.vue";

  const {mapState, mapActions} = createNamespacedHelpers('receipts');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "ReceiptTable",
    components: {
      InternalImagesViewer,
      ReceiptItemLocation, SearchCollapse, ReceiptSearch, ActionButtons, ArtisioTable, DeleteButton},
    data() {
      return {
        exportAttributes: {
          receipt_no: i18n.t('Receipt'),
          'customer.display_name': i18n.t('Customer'),
          date: i18n.t('Date'),
          description: i18n.t('Description'),
          total_items: i18n.t('Items'),
          total_reserve: i18n.t('Reserve'),
          enable_multiple_locations: i18n.t('Multi Item-Location')
        },
        selectedItems: new Set(),
        qrCodeGenerationLoading: false
      }
    },
    computed: {
      ...mapState({
        tableData: state => state.tableData[state.tableSource],
        toggleColumns: state => state.tableData[state.tableSource].toggleColumns,
        tableSource: state => state.tableSource,
      }),
      ...globalMapState({
        receiptStatuses: state => state.receiptStatuses,
      }),
      tableFields() {
        return cloneDeep(this.toggleColumns.fields).filter(f => f.isVisible);
      },
      extraData() {
        return {
          ...this.toggleColumns,
          excludeColumns: [],
          tableName: `${RECEIPTS_TABLE_NAME}.${this.tableSource}`,
        }
      },
    },
    methods: {
      ...mapActions(['getTableData', 'showModal', 'removeReceipt', 'getModalObject', 'batchRemoveReceipts', 'updateToggleColumns', 'updateAttachmentsCurrentTab']),
      async onViewClick(receipt) {
        this.$router.push({
          name: 'receipt.view',
          params: {id: receipt.uuid}
        });
      },
      onEditClick(item) {
        this.showModal({object: item, source: SOURCE_RECEIPT_TABLE})
        this.getModalObject({uuid: item.uuid, source: SOURCE_RECEIPT_TABLE});
      },
      async onRemoveClick(receipt) {
        this.$confirm(
          this.$i18n.t("Are you sure you want to delete receipt {receipt_no}?", {receipt_no: receipt.receipt_no}),
          this.$i18n.t('Receipt {receipt_no} was successfully deleted', {receipt_no: receipt.receipt_no}),
          this.removeReceipt,
          receipt
        );
      },
      async generateQrCodes(items) {
        let uuids = [];
        items.forEach(item => uuids.push(item.uuid));
        this.qrCodeGenerationLoading = true;
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {title: this.$i18n.t('Receipt QR Codes')});
        let response = await QrCodeService.batchGenerateQrCode({model_type: 'receipt', model_uuids: uuids})
        this.qrCodeGenerationLoading = false;
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            filename: this.$i18n.t('Receipt QR Codes'), pdf_url: response.body.url
          })
        }
      },
      async onBatchRemoveClick(items) {
        let uuids = [];
        items.forEach(item => uuids.push(item.uuid));

        let success = await this.$confirm(this.$i18n.t("Are you sure you want to delete selected receipts?"));
        if (success) {
          let response = await this.batchRemoveReceipts(uuids)
          if (response.success) {
            this.$toast('success', this.$i18n.t('Receipt(s) were successfully deleted'));
            this.getTableData();
          } else {
            if (response.body.non_field_errors) {
              this.$errorDialog(response.body.non_field_errors[0], this.$i18n.t('Bulk Delete Validation'));
            } else if (response.body.errors) {
              const listItemsArr = [];
              response.body.errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
              this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
            }
          }
        }

      },
      getDepartmentValue(item) {
        if (!item.department) return ''
        return this.$options.filters.masterLanguage(item.department.name)
      },
      getCalculatedCommissions(commissions) {
        let data = helperService.reverseCalculateTiers({commissions: cloneDeep(commissions)})
        return data.commissions
      },
      getStatusLabel(val) {
        if (val && this.receiptStatuses && this.receiptStatuses.length > 0) {
          let obj = this.receiptStatuses.find(s => s.value === val);
          return obj ? obj.label : val
        }
        return val
      },
      getDynamicFieldValue(item, field) {
        return this.$options.filters.masterLanguage(item.dynamic_fields)[field]
      },
      async onAttachmentsViewClick(receipt) {
        await this.updateAttachmentsCurrentTab(1)
        this.$router.push({name: 'receipt.attachments', params: {id: receipt.uuid}})
      }
    },
    mounted() {
      this.getTableData();
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .attachments-numbering {
    z-index: 10;
    top: -3px;
    right: -7px;
    margin: 0;
    border-radius: 100%;
    padding: 1.5px 3px;
    font-size: 10px;
    position: absolute;
    background-color: #1ec973;
    color: white;
  }

  .artisio-table {
    /deep/ {
      .description-col {
        max-width: 500px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .receipt-no-col {
        min-width: 100px;
      }

      .customer-col {
        min-width: 100px;
      }

      .date-col {
        min-width: 100px;
      }

      .items-col {
        min-width: 100px;
      }

      .reserve-col {
        min-width: 100px;
      }

      .department-col {
        min-width: 100px;
      }

      .commissions-col {
        min-width: 100px;
      }

      .minimum-commission_col {
        min-width: 100px;
      }

      .insurance-rate-col {
        min-width: 100px;
      }

      .lot-handling-charge-col {
        min-width: 100px;
      }

      .sold-items-col {
        min-width: 100px;
      }

      .created-data-col {
        min-width: 190px;
      }

      .updated-data-col {
        min-width: 190px;
      }

      .dynamic-fields-col {
        min-width: 100px;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }
</style>

